import api from "@/api";
import converters from "@/converters";
import type { DepartmentFragment } from "@/graphql";
import { Module_Type } from "@/graphql/generated-types";
import isDataReady from "@/helpers/requests/isDataReady";
import type { ICachedData } from "@/interfaces";
import { computed, ref } from "vue";
import type { InstitutionMinimal } from "@/extendedTypes.ts";

type ExternalDepartments = {
  departments: DepartmentFragment[];
  institutions: InstitutionMinimal[];
};

const DEFAULT_DATA: ICachedData<ExternalDepartments> = {
  status: "none",
  data: { departments: [], institutions: [] },
};

const externalDepartments = ref<
  Record<Module_Type, ICachedData<ExternalDepartments>>
>({
  [Module_Type.Activity]: { ...DEFAULT_DATA },
  [Module_Type.Booking]: { ...DEFAULT_DATA },
  [Module_Type.Gallery]: { ...DEFAULT_DATA },
  [Module_Type.Groups]: { ...DEFAULT_DATA },
  [Module_Type.Library]: { ...DEFAULT_DATA },
  [Module_Type.Meal]: { ...DEFAULT_DATA },
  [Module_Type.Meeting]: { ...DEFAULT_DATA },
  [Module_Type.MyDay]: { ...DEFAULT_DATA },
  [Module_Type.News]: { ...DEFAULT_DATA },
  [Module_Type.Profiles]: { ...DEFAULT_DATA },
  [Module_Type.WhatsHappening]: { ...DEFAULT_DATA },
  [Module_Type.WorkSchedule]: { ...DEFAULT_DATA },
});

export function useExternalDepartments(type: Module_Type) {
  const init = async () => {
    const item = externalDepartments.value[type];

    // We should fetch only once
    if (item.status === "loaded") {
      return;
    }

    item.status = "loading";
    const [data, institutions] = await Promise.all([
      api.departmets.getExternalDepartments(type as any),
      api.institution.getAllInstitutions(),
    ]);

    item.data.departments =
      data.institutions?.getMyInstitution.externalDepartments || [];
    item.data.institutions = institutions || [];
    item.status = "loaded";
  };

  init();

  const externalDepartmentList = computed(() => {
    const institutions = externalDepartments.value[type].data.institutions;
    return externalDepartments.value[type].data.departments.map((item) => {
      return converters.departments.toMultiSelectItem(item, institutions);
    });
  });

  const externalDepartmentOptions = computed(() => {
    const institutions = externalDepartments.value[type].data.institutions;
    return externalDepartments.value[type].data.departments.map((item) => {
      return converters.departments.toOption(item, institutions);
    });
  });

  const loading = computed(() => {
    return externalDepartments.value[type].status === "loading";
  });

  const status = computed(() => {
    return externalDepartments.value[type].status;
  });

  const isReady = () => isDataReady(status);

  return {
    externalDepartments,
    externalDepartmentList,
    externalDepartmentOptions,
    loading,
    isReady,
  };
}
